import { EventStatus, UserRole } from '@sportspredict/shared';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { ReactNode, useCallback, useContext, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router';
import {
	ScrollToTop,
	useSignOut,
	useIsMobile,
	useNotifications,
	useAchievements,
	useHiatusDetection,
	useTimezone,
	usePopupCategories,
	useNavigationConfig,
	useOneSignalInit,
	useIsRegisteredUser,
	usePointerActivation,
	useIsSigningOut,
} from '@/hooks';
import {
	useGetProfileQuery,
	useGetEventQuery,
	useGetUserAchievementsQuery,
	useUpdateCookieSettingsMutation,
	useGetNotViewedPopupQuery,
	useMarkPopupAsSeenMutation,
} from '@/store/api';
import { WebSidebar } from '@/components/web-version/web-sidebar';
import { LegacyWebSidebar } from '@/components/web-version/legacy-web-sidebar';
import { COOKIE_CONSENT_KEY } from '@/components/core/cookie-preferences.shared';
import { SocketContext } from '@/contexts/socket.context';
import { GlobalStyles } from '@/components/layout/GlobalStyles';
import { LayoutHeader } from '@/components/layout/LayoutHeader';
import { LayoutModals } from '@/components/layout/LayoutModals';
import { MobileSidebar } from '@/components/layout/MobileSidebar';
import { MobileBottomNav } from '@/components/layout/MobileBottomNav';
import { LegacyMobileBottomNav } from '@/components/layout/LegacyMobileBottomNav';
import { LiveNotificationsList } from '@/components/layout/LiveNotificationsList';
import { LayoutMain } from '@/components/layout/LayoutMain';
import { SignOutLoadingOverlay } from '@/components/layout/SignOutLoadingOverlay';
import { LIGHT_SHELL_ROUTES, NO_LAYOUT_PAGES, PAGES_WITHOUT_NAV, ROUTES } from '@/constants';
import { COURSES_PAGE_NAME } from '@/modules/courses/constants/courses.constants';
import { useDispatch, useSelector } from 'react-redux';
import { openAuthModal, closeAuthModal } from '@/store/slices/auth-modal.slice';
import { buildAuthRedirectUrl } from '@/utils';
import { ProbabilityEventHeader } from '@/modules/probability-event/components/layout/probability-event-header.layout';
import { ProbabilityEventSecondaryHeader } from '@/modules/probability-event/components/layout/probability-event-secondary-header.layout';
import { ProbabilityEventWebHeader } from '@/modules/probability-event/components/layout/probability-event-web-header.layout';
import { ProbabilityEventBottomNav } from '@/modules/probability-event/components/layout/probability-event-bottom-nav.layout';
import { useAnsweringMode } from '@/modules/probability-event/hooks/use-answering-mode.hook';
import {
	getProbabilityEventIdFromPath,
	getProbabilityEventFriendsPath,
	getProbabilityEventTabPath,
	isProbabilityEventEditProfilePath,
	PROBABILITY_EVENT_PAGE_NAME,
	PROBABILITY_EVENT_TABS,
} from '@/modules/probability-event/utils/probability-event.utils';
import { CookieConsent } from '@/components/core/CookieConsent';
import { ResourcesGuideProvider } from '@/modules/probability-event/contexts/resources-guide.context';
import { ProbabilityEventBanner } from '@/modules/probability-event/components/banner';

/** Lobby tabs that show the event banner; Performance is matched by its own path. */
const PROBABILITY_EVENT_BANNER_TABS = [
	PROBABILITY_EVENT_TABS.HOME,
	PROBABILITY_EVENT_TABS.LEADERBOARD,
	PROBABILITY_EVENT_TABS.FRIENDS,
	PROBABILITY_EVENT_TABS.PROFILE,
	PROBABILITY_EVENT_TABS.BOTS,
];
/** Inside the app the mobile header is 84px, taller than the 54px the layout reserves for it. */
const APP_HEADER_OFFSET_PX = 30;
/**
 * Mobile only: how far below the top of its area each banner page's first
 * content sits on its own, on the web and in the app (measured). The banner
 * uses it to leave exactly 16px between itself and that content.
 */
const PROBABILITY_EVENT_BANNER_PAGE_TOP_SPACE: Record<string, { web: number; app: number }> = {
	[PROBABILITY_EVENT_TABS.HOME]: { web: 40, app: 70 },
	[PROBABILITY_EVENT_TABS.LEADERBOARD]: { web: 28, app: 66 },
	[PROBABILITY_EVENT_TABS.FRIENDS]: { web: 32, app: 40 },
	[PROBABILITY_EVENT_TABS.PROFILE]: { web: 32, app: 62 },
	[PROBABILITY_EVENT_TABS.BOTS]: { web: 32, app: 62 },
	'performance-summary': { web: 16, app: 16 },
};
/**
 * Desktop: each banner page's content container (max width + side padding), so
 * the banner's category and title line up with the page content's left edge.
 */
const PROBABILITY_EVENT_BANNER_DESKTOP_CONTAINER: Record<string, string> = {
	[PROBABILITY_EVENT_TABS.HOME]: 'lg:max-w-[1314px] lg:px-6',
	[PROBABILITY_EVENT_TABS.LEADERBOARD]: 'lg:max-w-[936px] lg:px-5',
	[PROBABILITY_EVENT_TABS.FRIENDS]: 'lg:max-w-[620px] lg:px-8',
	[PROBABILITY_EVENT_TABS.PROFILE]: 'lg:max-w-[1046px] lg:px-5',
	[PROBABILITY_EVENT_TABS.BOTS]: 'lg:max-w-[1306px] lg:px-5',
	// The page itself has no side padding at 1024px; keep the text off the edge.
	'performance-summary': 'lg:max-w-[1232px] lg:px-4',
};

export default function Layout({
	children,
	currentPageName: providedCurrentPageName,
}: {
	children: ReactNode;
	currentPageName: string;
}) {
	const navigate = useNavigate();
	const location = useLocation();
	const normalizedPath = location.pathname.replace(/\/+$/, '') || ROUTES.ROOT;
	const isHomePage = [ROUTES.ROOT, ROUTES.HOME].includes(normalizedPath.toLowerCase());
	const currentPageName = isHomePage ? 'Home' : providedCurrentPageName;
	const [searchParams] = useSearchParams();
	const isRegisteredUser = useIsRegisteredUser();
	const { data: user, isLoading } = useGetProfileQuery(isRegisteredUser ? undefined : skipToken);
	const isProbabilityEventPage = currentPageName === PROBABILITY_EVENT_PAGE_NAME;
	const isLobbyDetailsPage = currentPageName === 'LobbyDetails';
	const isCoursesPage = currentPageName === COURSES_PAGE_NAME;
	const isLightShellPage =
		isHomePage || isCoursesPage || LIGHT_SHELL_ROUTES.includes(normalizedPath.toLowerCase());
	const { data: userAchievements } = useGetUserAchievementsQuery(
		user && user.role === UserRole.User && !isProbabilityEventPage ? undefined : skipToken
	);

	const [sidebarOpen, setSidebarOpen] = React.useState(false);
	const [isWebSidebarExpanded, setIsWebSidebarExpanded] = React.useState(true);
	const isMobile = useIsMobile();
	const signOut = useSignOut();
	const isSigningOut = useIsSigningOut();
	const userError = false;
	const [accountOpen, setAccountOpen] = React.useState(false);
	const [howToOpen, setHowToOpen] = React.useState(false);
	const [showCrowdLockModal, setShowCrowdLockModal] = React.useState(false);
	const dispatch = useDispatch();
	const authModalOpen = useSelector((state: any) => state.authModal.open);

	const [updateCookieSettings] = useUpdateCookieSettingsMutation();

	const socket = useContext(SocketContext);

	const { liveNotifications, notificationsReady, setNotificationsReady, notifyOnce, handleClose } =
		useNotifications(user, socket);

	const { showMilestoneModal, pendingAchievement, handleMilestoneClose } =
		useAchievements(userAchievements);

	const { showHiatusModal, setShowHiatusModal } = useHiatusDetection(
		user,
		notificationsReady,
		notifyOnce
	);

	const { currentTimezone } = useTimezone();
	const { popupCategories, setPopupCategories } = usePopupCategories(user);
	const { data: notViewedPopup } = useGetNotViewedPopupQuery(
		user?.role === UserRole.User ? undefined : skipToken
	);
	const [markPopupAsSeen] = useMarkPopupAsSeenMutation();
	const [productUpdateOpen, setProductUpdateOpen] = React.useState(false);

	useEffect(() => {
		if (!notViewedPopup) return;
		const shouldShow = isMobile ? notViewedPopup.isMobile : notViewedPopup.isDesktop;
		if (!shouldShow) return;

		const scheduledAt = notViewedPopup.scheduledDate
			? new Date(notViewedPopup.scheduledDate)
			: null;
		const delay = scheduledAt ? scheduledAt.getTime() - Date.now() : 0;

		if (delay <= 0) {
			setProductUpdateOpen(true);
			return;
		}

		const timer = setTimeout(() => setProductUpdateOpen(true), delay);
		return () => clearTimeout(timer);
	}, [notViewedPopup, isMobile]);

	const handleProductUpdateClose = async () => {
		setProductUpdateOpen(false);
		if (notViewedPopup) {
			try {
				await markPopupAsSeen(notViewedPopup.id);
			} catch {
				// noop
			}
		}
	};
	const { bottomNav, navigation } = useNavigationConfig(user, isMobile);
	const probabilityEventId = isProbabilityEventPage
		? getProbabilityEventIdFromPath(location.pathname)
		: null;
	const isProbabilityEventMarketsPage =
		isProbabilityEventPage && normalizedPath.endsWith(`/${PROBABILITY_EVENT_TABS.MARKETS}`);
	// List answering mode pins the In Game screen to the viewport, so the layout shell
	// must not add its own min-h-screen / bottom padding on top of it (JWB-4688).
	const { mode: answeringMode } = useAnsweringMode();
	const isProbabilityEventListModePage = isProbabilityEventMarketsPage && answeringMode === 'list';
	const isProbabilityEventFriendsSearchPage =
		isProbabilityEventPage && normalizedPath.endsWith(`/${PROBABILITY_EVENT_TABS.FRIENDS}/search`);
	const isProbabilityEventShareProfilePage =
		isProbabilityEventPage &&
		normalizedPath.endsWith(`/${PROBABILITY_EVENT_TABS.FRIENDS}/share-profile`);
	const isProbabilityEventHistoryPage =
		isProbabilityEventPage && location.pathname.includes('/history/');
	const isProbabilityEventAchievementsPage =
		isProbabilityEventPage && normalizedPath.endsWith(`/${PROBABILITY_EVENT_TABS.ACHIEVEMENTS}`);
	const isProbabilityEventEditProfilePage =
		isProbabilityEventPage && isProbabilityEventEditProfilePath(location.pathname);
	const isProbabilityEventBotDetailsPage =
		isProbabilityEventPage && /\/bot\/[^/]+/.test(normalizedPath);
	const isProbabilityEventPerformanceSummaryPage =
		isProbabilityEventPage && normalizedPath.endsWith('/performance-summary');
	const isProbabilityEventNewsfeedPage =
		isProbabilityEventPage && normalizedPath.endsWith('/newsfeed');
	const isMobileEditProfilePage = isProbabilityEventEditProfilePage && isMobile;
	const { data: probabilityEvent } = useGetEventQuery(probabilityEventId || '', {
		skip: !probabilityEventId,
		refetchOnMountOrArgChange: true,
	});
	const isProbabilityEventPreStart = probabilityEvent?.status === EventStatus.Upcoming;
	const isProbabilityEventHomePage =
		Boolean(probabilityEventId) &&
		normalizedPath === getProbabilityEventTabPath(probabilityEventId!, PROBABILITY_EVENT_TABS.HOME);
	// The event banner sits on the main lobby pages only (Home, Leaderboard,
	// Following, Profile, Bots, Performance), not on focused or detail screens.
	const probabilityEventBannerPage = !probabilityEventId
		? undefined
		: isProbabilityEventPerformanceSummaryPage
			? 'performance-summary'
			: PROBABILITY_EVENT_BANNER_TABS.find(
					(tab) => normalizedPath === getProbabilityEventTabPath(probabilityEventId, tab)
				);

	useEffect(() => {
		if (!isProbabilityEventPreStart || !probabilityEventId) return;

		const homePath = getProbabilityEventTabPath(probabilityEventId, PROBABILITY_EVENT_TABS.HOME);
		const profilePath = getProbabilityEventTabPath(
			probabilityEventId,
			PROBABILITY_EVENT_TABS.PROFILE
		);
		// Pre-start, keep users on Home — EXCEPT the Profile flow, where bots are
		// created/connected before markets open (JWB-4624). Profile hosts the My
		// Bots tab (a view state on this same route); bot detail is /bot/:id.
		const isProfileFlow = normalizedPath === profilePath || isProbabilityEventBotDetailsPage;
		if (normalizedPath !== homePath && !isProfileFlow) {
			navigate(homePath, { replace: true });
		}
	}, [
		isProbabilityEventPreStart,
		navigate,
		normalizedPath,
		probabilityEventId,
		isProbabilityEventBotDetailsPage,
	]);

	useOneSignalInit(user?.id);

	const isCrowdLockOpen = searchParams.get('isCrowdLock') === 'true';

	const handleLogin = useCallback(() => {
		if (isMobile) {
			dispatch(openAuthModal());
		} else {
			navigate(buildAuthRedirectUrl());
		}
	}, [isMobile, dispatch, navigate]);

	const loginPointerProps = usePointerActivation(handleLogin);

	useEffect(() => {
		if (!user) return;

		if (isRegisteredUser) {
			Object.keys(localStorage)
				.filter((key) => key.startsWith('introSeen:guest'))
				.forEach((key) => localStorage.removeItem(key));
		}
	}, [user, isRegisteredUser]);

	useEffect(() => {
		if (!user || user.cookieSettings) return;

		const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
		if (!raw) return;

		try {
			const stored = JSON.parse(raw);
			if (stored && typeof stored === 'object') {
				updateCookieSettings(stored);
			}
		} catch (error) {
			console.error(error);
		}
	}, [user, updateCookieSettings]);

	if (!children) return null;
	if (NO_LAYOUT_PAGES.includes(currentPageName)) return <>{children}</>;

	const ChildComponent = (children as any)?.type;
	if (ChildComponent?.noLayout) return <>{children}</>;
	if (isSigningOut) {
		return (
			<>
				<GlobalStyles />
				<SignOutLoadingOverlay isHomePage={isHomePage} />
			</>
		);
	}

	// Popups that appear on their own, so an unrequested hint has to yield the screen to
	// them. Modals the user opens by hand are not here: the coach mark's own backdrop
	// takes the tap that would have opened them, so the two cannot overlap.
	const termsAccepted = !user || user.viewed_popups?.accepted_latest_terms !== false;
	const hasBlockingPopup =
		!termsAccepted ||
		showHiatusModal ||
		showMilestoneModal ||
		popupCategories.length > 0 ||
		productUpdateOpen;

	return (
		<ResourcesGuideProvider
			eventId={probabilityEventId}
			userId={user?.id}
			enabled={isProbabilityEventPage}
			showCoachMarkOnLoad={isProbabilityEventHomePage}
			hasBlockingPopup={hasBlockingPopup}
		>
			<div
				className={`min-h-screen relative bg-cover bg-center bg-no-repeat text-white overflow-x-hidden${
					isLobbyDetailsPage ? ' lg:bg-[#101423]' : ''
				}${
					isHomePage
						? isMobile
							? ' home-inter-font bg-[#f4f6fc]'
							: ' home-inter-font bg-[radial-gradient(circle_at_8%_75%,rgba(223,210,255,0.42),transparent_29%),linear-gradient(135deg,#f7f9ff_0%,#fff_53%,#f8f4ff_100%)]'
						: isCoursesPage
							? ' bg-[#f8f9fc]'
							: isLightShellPage
								? ' bg-[radial-gradient(circle_at_8%_75%,rgba(223,210,255,0.42),transparent_29%),linear-gradient(135deg,#f7f9ff_0%,#fff_53%,#f8f4ff_100%)]'
								: ''
				}`}
			>
				<ScrollToTop />
				<GlobalStyles />

				{/* Web Sidebar */}
				{!isProbabilityEventPage &&
					!isMobile &&
					!PAGES_WITHOUT_NAV.includes(currentPageName) &&
					(isLightShellPage ? (
						<WebSidebar
							bottomNav={bottomNav}
							navigation={navigation}
							currentPageName={currentPageName}
							currentTimezone={currentTimezone}
							user={user}
							isLoadingUser={isLoading}
							signOut={signOut}
							onAccountClick={() => setAccountOpen(true)}
							onHowToPlayClick={() => setHowToOpen(true)}
							isRegisteredUser={isRegisteredUser}
							isExpanded={isWebSidebarExpanded}
							onExpandedChange={setIsWebSidebarExpanded}
						/>
					) : (
						<LegacyWebSidebar
							bottomNav={bottomNav}
							navigation={navigation}
							currentPageName={currentPageName}
							currentTimezone={currentTimezone}
							user={user}
							isLoadingUser={isLoading}
							signOut={signOut}
							onAccountClick={() => setAccountOpen(true)}
							onHowToPlayClick={() => setHowToOpen(true)}
							setSidebarOpen={setSidebarOpen}
							isRegisteredUser={isRegisteredUser}
						/>
					))}

				{/* Live Notifications */}
				<LiveNotificationsList
					notifications={liveNotifications}
					onClose={handleClose}
					isMobile={isMobile}
				/>

				{/* Mobile Sidebar */}
				{!isProbabilityEventPage && (
					<MobileSidebar
						isOpen={sidebarOpen}
						onClose={() => setSidebarOpen(false)}
						navigation={navigation}
						currentPageName={currentPageName}
						currentTimezone={currentTimezone}
						user={user}
						isLoading={isLoading}
						userError={userError}
						signOut={signOut}
						onAccountClick={() => {
							setAccountOpen(true);
							setSidebarOpen(false);
						}}
						onHowToPlayClick={() => {
							setHowToOpen(true);
							setSidebarOpen(false);
						}}
						loginPointerProps={loginPointerProps}
						isMobile={isMobile}
						isRegisteredUser={isRegisteredUser}
					/>
				)}

				{/* Header */}
				{isProbabilityEventPage ? (
					<>
						<div className="lg:hidden">
							{isProbabilityEventAchievementsPage ||
							isProbabilityEventEditProfilePage ||
							isProbabilityEventHistoryPage ||
							isProbabilityEventBotDetailsPage ||
							isProbabilityEventNewsfeedPage ? null : isProbabilityEventMarketsPage ? (
								<ProbabilityEventSecondaryHeader title="In Game" />
							) : isProbabilityEventFriendsSearchPage ? (
								<ProbabilityEventSecondaryHeader
									title="Search People"
									url={
										probabilityEventId
											? getProbabilityEventFriendsPath(probabilityEventId)
											: undefined
									}
									variant="close"
								/>
							) : isProbabilityEventShareProfilePage ? (
								<ProbabilityEventSecondaryHeader title="Share Profile" />
							) : isProbabilityEventPerformanceSummaryPage ? (
								<ProbabilityEventSecondaryHeader title="Performance Summary" />
							) : (
								<ProbabilityEventHeader
									user={user}
									eventName={probabilityEvent?.title}
									eventId={probabilityEventId || undefined}
								/>
							)}
						</div>
						<div className="hidden lg:block">
							<ProbabilityEventWebHeader
								user={user}
								eventName={probabilityEvent?.title}
								eventId={probabilityEventId || undefined}
								isPreStart={isProbabilityEventPreStart}
							/>
						</div>
					</>
				) : (
					<LayoutHeader
						isMobile={isMobile}
						isLightShellPage={isLightShellPage}
						currentPageName={currentPageName}
						user={user}
						onSidebarOpen={() => setSidebarOpen(true)}
						setNotificationsReady={setNotificationsReady}
						isCrowdLockOpen={isCrowdLockOpen}
						isRegisteredUser={isRegisteredUser}
					/>
				)}

				{/* Main Content */}
				<LayoutMain
					isLightShellPage={isLightShellPage}
					currentPageName={currentPageName}
					isLoading={isLoading}
					userError={userError}
					loginPointerProps={loginPointerProps}
					isCrowdLockOpen={isCrowdLockOpen}
					isRegisteredUser={isRegisteredUser}
					isWebSidebarExpanded={isWebSidebarExpanded}
					isProbabilityLayout={isProbabilityEventPage}
					isProbabilityAchievementsPage={isProbabilityEventAchievementsPage}
					isProbabilityEventEditProfilePage={isMobileEditProfilePage}
					isProbabilityFocusedPage={
						isProbabilityEventFriendsSearchPage || isProbabilityEventShareProfilePage
					}
					isProbabilityPinnedPage={isProbabilityEventListModePage}
					isProbabilityPerformanceSummaryPage={isProbabilityEventPerformanceSummaryPage}
					isProbabilityNewsfeedPage={isProbabilityEventNewsfeedPage}
					banner={
						probabilityEventBannerPage && probabilityEventId ? (
							<ProbabilityEventBanner
								eventId={probabilityEventId}
								eventTitle={probabilityEvent?.title}
								categoryId={probabilityEvent?.category_id}
								appHeaderOffsetPx={
									isProbabilityEventPerformanceSummaryPage ? 0 : APP_HEADER_OFFSET_PX
								}
								pageTopSpacePx={PROBABILITY_EVENT_BANNER_PAGE_TOP_SPACE[probabilityEventBannerPage]}
								desktopContainerClassName={
									PROBABILITY_EVENT_BANNER_DESKTOP_CONTAINER[probabilityEventBannerPage]
								}
							/>
						) : null
					}
				>
					{children}
				</LayoutMain>

				{/* Mobile Bottom Navigation */}
				{isProbabilityEventPage &&
				!isProbabilityEventAchievementsPage &&
				!isProbabilityEventEditProfilePage &&
				!isProbabilityEventFriendsSearchPage &&
				!isProbabilityEventNewsfeedPage &&
				!isProbabilityEventShareProfilePage ? (
					<div className="lg:hidden">
						<ProbabilityEventBottomNav
							user={user}
							authModalOpen={authModalOpen}
							isPreStart={isProbabilityEventPreStart}
						/>
					</div>
				) : !isProbabilityEventPage ? (
					isLightShellPage ? (
						<MobileBottomNav
							show={
								!PAGES_WITHOUT_NAV.includes(currentPageName) &&
								isMobile &&
								!isCrowdLockOpen &&
								!authModalOpen
							}
							bottomNav={bottomNav}
							currentPageName={currentPageName}
							onNavigate={navigate}
							onLoginClick={handleLogin}
							loginPointerProps={loginPointerProps}
							isRegisteredUser={isRegisteredUser}
						/>
					) : (
						<LegacyMobileBottomNav
							show={
								!PAGES_WITHOUT_NAV.includes(currentPageName) &&
								isMobile &&
								!isCrowdLockOpen &&
								!authModalOpen
							}
							bottomNav={bottomNav}
							currentPageName={currentPageName}
							onNavigate={navigate}
							loginPointerProps={loginPointerProps}
							isRegisteredUser={isRegisteredUser}
						/>
					)
				) : null}

				<CookieConsent />

				{/* All Modals */}
				<LayoutModals
					accountOpen={accountOpen}
					onAccountChange={setAccountOpen}
					howToOpen={howToOpen}
					onHowToChange={setHowToOpen}
					showHiatusModal={showHiatusModal}
					onHiatusClose={() => setShowHiatusModal(false)}
					showMilestoneModal={showMilestoneModal}
					onMilestoneClose={handleMilestoneClose}
					pendingAchievement={pendingAchievement}
					user={user}
					showCrowdLockModal={showCrowdLockModal}
					onCrowdLockClose={() => setShowCrowdLockModal(false)}
					popupCategories={popupCategories}
					onPopupCategoriesChange={setPopupCategories}
					setSidebarOpen={setSidebarOpen}
					authModalOpen={authModalOpen}
					onAuthModalChange={(open: boolean) => dispatch(open ? openAuthModal() : closeAuthModal())}
					productUpdatePopup={notViewedPopup ?? null}
					productUpdateOpen={productUpdateOpen}
					onProductUpdateClose={handleProductUpdateClose}
				/>
			</div>
		</ResourcesGuideProvider>
	);
}
